import {Comparator, Equality, NumberKeyed, Predicate, StringKeyed, Transform} from "../../types/GenericTypes";
import {IFilterFilter} from "angular";
import {ObjectUtil} from "./ObjectUtil";
import {Optional} from "./Optional";
import {identity} from "./UtilFunctions";

const DEFAULT_MATCHER: Equality<any> = (a, b) => a === b;
const DEFAULT_TRANSFORM: Transform<any, any> = identity();

export class ArrayUtil {

    public static matchesAny(filter: IFilterFilter,
                             arr: ReadonlyArray<any>,
                             ...filterConfig: Array<StringKeyed<string>>): boolean {
        for (const f of filterConfig) {
            const filtered = filter([...arr], f);
            if (filtered.length > 0) {
                return true;
            }
        }
        return false;
    }

    public static clone<T>(arr: ReadonlyArray<T>): Array<T> {
        return arr.map(item => ObjectUtil.copy(item));
    }

    public static replaceBy<T>(arr: ReadonlyArray<T>, item: T, predicate: Predicate<T>): ReadonlyArray<T> {
        const index = arr.findIndex(predicate);
        if (index === -1) {
            return arr.slice().concat(item);
        } else {
            const copy = arr.slice();
            copy.splice(index, 1, item);

            return copy;
        }
    }

    public static matches<T>(arr1: ReadonlyArray<T>,
                             arr2: ReadonlyArray<T>,
                             matcher: Equality<T> = DEFAULT_MATCHER): boolean {
        if (arr1.length !== arr2.length) {
            return false;
        }
        return this.includesAll(arr1, arr2, matcher) && this.includesAll(arr2, arr1, matcher);
    }

    public static includes<T>(arr: ReadonlyArray<T>, item: T, matcher: Equality<T> = DEFAULT_MATCHER): boolean {
        return arr.find((i) => matcher(i, item)) != null;
    }

    public static includesAll<T>(arr1: ReadonlyArray<T>,
                                 arr2: ReadonlyArray<T>,
                                 matcher: Equality<T> = DEFAULT_MATCHER): boolean {
        if ((arr1.length === 0) || (arr2.length === 0)) {
            return false;
        }
        return arr1.every(v1 => arr2.find(v2 => matcher(v1, v2)) != null);
    }

    /** Checks that the contents are equal and in the same order */
    public static matchesExact<T>(arr1: ReadonlyArray<T>,
                                  arr2: ReadonlyArray<T>,
                                  matcher: Equality<T> = DEFAULT_MATCHER): boolean {
        if (arr1.length !== arr2.length) {
            return false;
        }
        for (let i = 0; i < arr1.length; i++) {
            const val1 = arr1[i];
            const val2 = arr2[i];
            if (!matcher(val1, val2)) {
                return false;
            }
        }
        return true;
    }

    public static shuffle<T>(arr: Array<T>): Array<T> {
        const array = this.clone(arr);

        let currentIndex = array.length;
        let temp: T;
        let randomIndex: number;

        while (0 !== currentIndex) {
            randomIndex = Math.floor(Math.random() * currentIndex);
            currentIndex -= 1;

            temp = array[currentIndex];
            array[currentIndex] = array[randomIndex];
            array[randomIndex] = temp;
        }
        return array;
    }

    /* Create an array with numbers from 0 to (limit - 1) https://stackoverflow.com/a/33352604 */
    public static range(limit: number): Array<number> {
        return Array.from(Array(limit).keys());
    }

    public static groupBy<T>(arr: ReadonlyArray<T>, groupingFn: Transform<T, string>): StringKeyed<Array<T>>;
    public static groupBy<T>(arr: ReadonlyArray<T>, groupingFn: Transform<T, number>): NumberKeyed<Array<T>>;

    public static groupBy<T>(arr: ReadonlyArray<T>, groupingFn: Transform<T, any>): any {
        return arr.reduce((acc, next) => {
            const key = groupingFn(next);
            const group = acc[key] || [];
            group.push(next);
            acc[key] = group;
            return acc;
        }, {} as StringKeyed<Array<T>>);
    }

    public static mapBy<T, K>(arr: ReadonlyArray<T>, selectorFn: Transform<T, K>): Map<K, Array<T>> {
        const result = new Map<K, Array<T>>();
        arr.forEach((val) => {
            const key = selectorFn(val);
            const group = result.get(key) || [];
            group.push(val);
            result.set(key, group);
        });
        return result;
    }

    public static toMap<T>(arr: ReadonlyArray<T>, selectorFn: Transform<T, string>): StringKeyed<T>;
    public static toMap<T>(arr: ReadonlyArray<T>, selectorFn: Transform<T, number>): NumberKeyed<T>;

    public static toMap<T>(arr: ReadonlyArray<T>, selectorFn: Transform<T, string | number>): StringKeyed<any> {
        if (arr.length === 0) {
            return {};
        } else {
            return arr.map(el => ({[selectorFn(el)]: el}))
                      .reduce((acc, next) => Object.assign(acc, next));
        }
    }

    public static distinctBy<T, R>(arr: Array<T>, selector: Transform<T, R> = DEFAULT_TRANSFORM): Array<T> {
        return arr.filter((item, index, self) => {
            const itemValue = selector(item);
            const firstIndex = self.findIndex((i) => selector(i) === itemValue);
            return firstIndex === index;
        });
    }

    public static distinct<T>(arr: ReadonlyArray<T>, matcher: Equality<T> = DEFAULT_MATCHER): ReadonlyArray<T> {
        return arr.filter((item, index, self) => {
            const firstIndex = self.findIndex((si) => matcher(si, item));
            return firstIndex === index;
        });
    }

    public static min<T>(arr: ReadonlyArray<T>, comparator: Comparator<T>): Optional<T> {
        const sorted = Array.from(arr).sort(comparator);
        return Optional.of(sorted[0]);
    }

    public static max<T>(arr: ReadonlyArray<T>, comparator: Comparator<T>): Optional<T> {
        const sorted = Array.from(arr).sort(comparator).reverse();
        return Optional.of(sorted[0]);
    }

    public static split<T>(arr: ReadonlyArray<T>, selector: Predicate<T>): [ReadonlyArray<T>, ReadonlyArray<T>] {
        const match: Array<T> = [];
        const noMatch: Array<T> = [];

        arr.forEach((entry) => {
            if (selector(entry)) {
                match.push(entry);
            } else {
                noMatch.push(entry);
            }
        });

        return [match, noMatch];
    }

    /* The build in Array#isArray erases any generics on your Array */
    public static isArray<T>(value: any | ArrayLike<T>): value is ArrayLike<T> {
        return Array.isArray(value);
    }

    public static apply<T, R>(fn: Transform<T, R>): Transform<ReadonlyArray<T>, ReadonlyArray<R>> {
        return (arr) => arr.map(fn);
    }

    public static removeItem<T>(arr: T[], item: T): T[] {
        return arr.filter(element => element !== item);
    }

    public static removeItemInPlace<T>(arr: T[], item: T): T[] {
        const index = arr.indexOf(item);
        if (index > -1) {
            arr.splice(index, 1);
        }
        return arr;
    }
}
